import React, { useEffect, useState } from "react";
import { Save, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { adminApi } from "../../lib/api";
import { getIcon, iconNames } from "../../lib/icons";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";

const emptyValue = { title: "", icon: "CheckCircle2", text: "" };
const empty = { mission_heading: "", mission: "", mission_image: "", values: [] };

export default function AboutManager() {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.about.get().then((d) => setForm({ ...empty, ...d })).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setValue = (i, k, v) => setForm((f) => ({ ...f, values: f.values.map((val, idx) => (idx === i ? { ...val, [k]: v } : val)) }));
  const addValue = () => setForm((f) => ({ ...f, values: [...f.values, { ...emptyValue }] }));
  const removeValue = (i) => setForm((f) => ({ ...f, values: f.values.filter((_, idx) => idx !== i) }));
  const move = (i, dir) => setForm((f) => {
    const arr = [...f.values];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, values: arr };
  });

  const save = async () => {
    setSaving(true);
    try { await adminApi.about.update(form); toast.success("About page saved"); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-extrabold text-2xl text-navy">About Page</h1>
        <a href="/about" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange"><ExternalLink size={14} /> View page</a>
      </div>
      <p className="text-slate-500 text-sm mb-8">Edit the mission statement and the values shown on the About Us page.</p>

      <div className="max-w-2xl space-y-8">
        <div className="space-y-5 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h2 className="font-display font-bold text-navy border-b border-slate-100 pb-3">Our Mission</h2>
          <Field label="Mission heading">
            <input data-testid="about-mission-heading" value={form.mission_heading} onChange={(e) => set("mission_heading", e.target.value)} className={inputCls} placeholder="Empowering business owners, not baffling them" />
          </Field>
          <Field label="Mission statement">
            <textarea data-testid="about-mission" rows={4} value={form.mission} onChange={(e) => set("mission", e.target.value)} className={inputCls} />
          </Field>
          <ImageUpload label="Mission image (one large photo)" value={form.mission_image} onChange={(v) => set("mission_image", v)} hint="Shown large on the left of the mission section. A landscape/portrait team or office photo works well." />
        </div>

        <div className="space-y-5 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h2 className="font-display font-bold text-navy">Our Values</h2>
            <button data-testid="about-add-value" onClick={addValue} className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange hover:text-orange-dark"><Plus size={15} /> Add value</button>
          </div>

          {form.values.length === 0 && <p className="text-sm text-slate-400">No values yet — click "Add value" to create one.</p>}

          <div className="space-y-4">
            {form.values.map((v, i) => {
              const Icon = getIcon(v.icon);
              return (
                <div key={i} data-testid={`about-value-${i}`} className="rounded-xl border border-slate-200 p-4 bg-slate-50/60">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-lg bg-orange/10 flex items-center justify-center shrink-0 mt-0.5"><Icon size={18} className="text-orange" /></span>
                    <div className="flex-1 space-y-3">
                      <div className="grid sm:grid-cols-[1fr_180px] gap-3">
                        <input data-testid={`about-value-title-${i}`} value={v.title} onChange={(e) => setValue(i, "title", e.target.value)} className={inputCls} placeholder="Value title" />
                        <select data-testid={`about-value-icon-${i}`} value={v.icon} onChange={(e) => setValue(i, "icon", e.target.value)} className={inputCls}>
                          {iconNames.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                      <textarea data-testid={`about-value-text-${i}`} rows={2} value={v.text} onChange={(e) => setValue(i, "text", e.target.value)} className={inputCls} placeholder="Short description" />
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30" title="Move up"><ChevronUp size={16} /></button>
                      <button onClick={() => move(i, 1)} disabled={i === form.values.length - 1} className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30" title="Move down"><ChevronDown size={16} /></button>
                      <button data-testid={`about-value-remove-${i}`} onClick={() => removeValue(i)} className="p-1.5 rounded-md hover:bg-red-50 text-red-500" title="Remove"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button data-testid="about-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-full transition-colors">
          <Save size={16} /> {saving ? "Saving..." : "Save page"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40 bg-white";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-navy mb-1.5">{label}</label>
    {children}
  </div>
);
